import { api, type ChangesView, type SourceRef } from '../api';
import { changeParts, editionLabel } from '../editions';
import { formatDateTime } from '../format';
import { useOpenDetail } from './detail-link';
import { Loading, Notice, useAsync } from './ui';

/**
 * "Was hat sich geändert" in the details: the version on screen against its
 * baseline, from the two stored versions only.
 *
 * Opened from an edition, the baseline is the version that edition compared
 * against when it was published, so an old edition keeps its own comparison.
 * Otherwise it is the card's preceding version. Rewording is labelled as such;
 * added sources are listed apart from developments.
 */
export function ChangesPanel({ versionId, edition }: { versionId: string; edition: string | null }) {
  const res = useAsync(() => api.cardChanges(versionId, edition).catch(() => api.cardChanges(versionId)), [versionId, edition]);
  const open = useOpenDetail();
  if (res.loading && !res.data) return <Loading label="Vergleich wird geladen…" />;
  if (res.error) return <Notice kind="warn">Der Vergleich mit der früheren Version konnte nicht geladen werden: {res.error.message}</Notice>;
  const v = res.data;
  if (!v || v.basis === 'none') return null;
  if (v.basis === 'first_report') {
    return (
      <section className="changes" aria-labelledby="changes-h">
        <h3 id="changes-h">Was ist neu</h3>
        <p className="muted small">In dieser Ausgabe zum ersten Mal: es gibt keine frühere Version, mit der verglichen wurde.</p>
      </section>
    );
  }
  const c = v.comparison;
  const stored = changeParts(v.change ?? undefined);
  return (
    <section className="changes" aria-labelledby="changes-h">
      <h3 id="changes-h">Was hat sich geändert</h3>
      <p className="muted small">{baselineText(v)}</p>
      <dl className="kv changes-versions">
        <dt>Jetzt</dt>
        <dd>
          Version {v.current.version} · erstellt {formatDateTime(v.current.created_at)}
        </dd>
        {v.baseline && (
          <>
            <dt>Vorher</dt>
            <dd>
              <button type="button" className="text-button" onClick={(e) => open(v.baseline!.id, e.currentTarget)}>
                Version {v.baseline.version}
              </button>{' '}
              · erstellt {formatDateTime(v.baseline.created_at)}
            </dd>
          </>
        )}
      </dl>

      {v.basis === 'edition' && stored.developments.length > 0 && (
        <>
          <h4 className="mini-h">Laut Ausgabe neu</h4>
          <ul className="points">
            {stored.developments.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </>
      )}

      {c?.crossfit && (
        <>
          <h4 className="mini-h">Workout und Buchungen</h4>
          {c.crossfit.changes.length > 0 ? (
            <ul className="points">
              {c.crossfit.changes.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          ) : (
            <p className="muted small">Unverändert. Die neuere Version ist nur eine spätere Lesung desselben Tages.</p>
          )}
        </>
      )}

      {c && !c.crossfit && (
        <>
          {c.same_sources && (
            <Notice kind="info" title="Dieselben Quellen">
              Beide Versionen beruhen auf denselben Quelltexten. Unterschiede sind Formulierung, keine neuen Fakten.
            </Notice>
          )}
          {c.stage_changes.length > 0 && (
            <>
              <h4 className="mini-h">Verfahrensstand</h4>
              <ul className="points">
                {c.stage_changes.map((s, i) => (
                  <li key={i}>
                    {s.from} → {s.to}
                    {s.title ? <span className="muted"> ({s.title})</span> : null}
                  </li>
                ))}
              </ul>
            </>
          )}
          {c.key_points_added.length > 0 && (
            <>
              <h4 className="mini-h">Neu in den Kernpunkten</h4>
              <ul className="points">
                {c.key_points_added.map((k, i) => (
                  <li key={i}>
                    {k.text}{' '}
                    <span className={`tag ${k.new_fact ? 'tag-accent' : 'tag-soft'}`}>{k.new_fact ? 'neue Angaben in den Quellen' : 'nur anders formuliert'}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {c.key_points_removed.length > 0 && (
            <>
              <h4 className="mini-h">Nicht mehr in den Kernpunkten</h4>
              <ul className="points muted">
                {c.key_points_removed.map((k, i) => (
                  <li key={i}>{k}</li>
                ))}
              </ul>
            </>
          )}
          <SourceList title="Neue Quellen" items={c.sources_added} note="Eine weitere Quelle allein heisst nicht, dass etwas Neues geschehen ist." />
          <SourceList title="Nicht mehr zitiert" items={c.sources_removed} />
          <SourceList title="Vom Verlag überarbeitet" items={c.sources_updated} />
          {c.key_points_added.length === 0 &&
            c.key_points_removed.length === 0 &&
            c.sources_added.length === 0 &&
            c.sources_removed.length === 0 &&
            c.stage_changes.length === 0 && (
              <p className="muted small">
                {stored.developments.length > 0
                  ? 'Die Kernpunkte sind gleich geblieben; die neue Angabe steht in der Zusammenfassung.'
                  : c.summary_changed || c.title_changed
                    ? 'Nur Titel oder Zusammenfassung sind anders formuliert.'
                    : 'Keine inhaltlichen Unterschiede.'}
              </p>
            )}
        </>
      )}
    </section>
  );
}

function SourceList({ title, items, note }: { title: string; items: SourceRef[]; note?: string }) {
  if (items.length === 0) return null;
  return (
    <>
      <h4 className="mini-h">{title}</h4>
      <ul className="plain small">
        {items.map((s) => (
          <li key={s.item_id}>
            {s.source_name || s.source_id}:{' '}
            {s.url ? (
              <a href={s.url} target="_blank" rel="noopener noreferrer">
                {s.title} ↗
              </a>
            ) : (
              s.title
            )}
            {s.published_at && <span className="muted"> · {formatDateTime(s.published_at)}</span>}
          </li>
        ))}
      </ul>
      {note && <p className="muted small">{note}</p>}
    </>
  );
}

/** Names the baseline for what it is, never "the version you read". */
function baselineText(v: ChangesView): string {
  if (v.basis === 'edition') {
    const where = v.baseline_edition ? editionLabel(v.baseline_edition) : 'einer früheren Ausgabe';
    const when = v.baseline_shown_at ? ` vom ${formatDateTime(v.baseline_shown_at)}` : '';
    const here = v.edition?.label ? `Die ${v.edition.label}` : 'Diese Ausgabe';
    return `${here} vergleicht mit der Version, die zuletzt in der ${where}${when} enthalten war.`;
  }
  return 'Vergleich mit der vorherigen Version dieser Karte.';
}
